import { useEffect, useMemo } from 'react';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import I18n from '@utils/i18n';
import FilterTypes from '../../../../constants/FilterTypes.ts';
import { actionTypes } from '../reducer';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

const useFilters = ({
  filters,
  dispatch,
  statusesForSelect,
  buildingId,
  accountId,
  inspectionId,
  workOrderId,
  categoriesForSelect,
  isQbSyncEnabled,
  isRecurrenceEnabled
}) => {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);

  const onChangeFilter = (fieldName) => (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
  };

  const onToggleFilter = (fieldName) => () => {
    dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
  };

  const onChangeDateFilter = (fieldName) => (dateFieldName, value) => {
    dispatch({
      type: actionTypes.DATE_RANGE_FILTER_CHANGED,
      field: fieldName,
      dateField: dateFieldName,
      value
    });
  };

  const filtersList = useMemo(() => {
    const list = [
      {
        field: 'building',
        locale: 'building',
        type: buildingId || inspectionId || workOrderId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
        active: filters.active.building,
        value: filters.values.building,
        onChange: onChangeFilter('building'),
        onToggle: onToggleFilter('building')
      },
      {
        field: 'status',
        locale: 'status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.status,
        value: filters.values.status,
        onChange: onChangeFilter('status'),
        onToggle: onToggleFilter('status'),
        optionsForSelect: statusesForSelect
      },
      {
        field: 'invoiceNumber',
        locale: 'invoice_number',
        type: 'textBox',
        active: filters.active.invoiceNumber,
        value: filters.values.invoiceNumber,
        onChange: onChangeFilter('invoiceNumber'),
        onToggle: onToggleFilter('invoiceNumber')
      },
      {
        field: 'account',
        locale: 'account',
        type:
          accountId || buildingId || inspectionId || workOrderId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
        active: filters.active.account,
        value: filters.values.account,
        onChange: onChangeFilter('account'),
        onToggle: onToggleFilter('account')
      },
      {
        field: 'overdue',
        locale: 'overdue',
        type: 'commonSelect',
        active: filters.active.overdue,
        value: filters.values.overdue,
        onChange: onChangeFilter('overdue'),
        onToggle: onToggleFilter('overdue'),
        optionsForSelect: [
          { value: '1', label: 'Overdue (+1 days)' },
          { value: '30', label: '30+ days overdue' },
          { value: '60', label: '60+ days overdue' },
          { value: '90', label: '90+ days overdue' }
        ]
      },
      {
        field: 'issueDate',
        locale: 'issue_date',
        type: 'dateRangePicker',
        active: filters.active.issueDate,
        values: filters.values.issueDate,
        onChange: onChangeDateFilter('issueDate'),
        onToggle: onToggleFilter('issueDate')
      },
      {
        field: 'dueDate',
        locale: 'due_date',
        type: 'dateRangePicker',
        active: filters.active.dueDate,
        values: filters.values.dueDate,
        onChange: onChangeDateFilter('dueDate'),
        onToggle: onToggleFilter('dueDate')
      },
      {
        field: 'paidDate',
        locale: 'paid_date',
        type: 'dateRangePicker',
        active: filters.active.paidDate,
        values: filters.values.paidDate,
        onChange: onChangeDateFilter('paidDate'),
        onToggle: onToggleFilter('paidDate')
      }
    ];

    if (isRecurrenceEnabled) {
      list.push({
        field: 'recurringInvoice',
        locale: 'recurring_invoice',
        type: 'booleanSelect',
        active: filters.active.recurringInvoice,
        value: filters.values.recurringInvoice,
        onChange: onChangeFilter('recurringInvoice'),
        onToggle: onToggleFilter('recurringInvoice')
      });
    }

    if (categoriesForSelect && categoriesForSelect.length > 0) {
      list.push({
        field: 'category',
        locale: 'category',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.category,
        value: filters.values.category,
        onChange: onChangeFilter('category'),
        onToggle: onToggleFilter('category'),
        optionsForSelect: categoriesForSelect
      });
    }

    if (isQbSyncEnabled) {
      list.push({
        field: 'linkedToQbo',
        locale: 'linked_to_qbo',
        type: 'booleanSelect',
        active: filters.active.linkedToQbo,
        value: filters.values.linkedToQbo,
        onChange: onChangeFilter('linkedToQbo'),
        onToggle: onToggleFilter('linkedToQbo')
      });
    }

    // IP-15498. Optional (off by default): "Sent" means an invoice_sent communication log exists,
    // i.e. it was emailed. Not gated on an integration, unlike linkedToQbo above.
    list.push({
      field: 'sent',
      locale: 'sent',
      type: 'booleanSelect',
      // BooleanSelect renders blank options without these -- the sibling recurringInvoice and
      // linkedToQbo entries omit them and show two empty rows.
      trueLabel: I18n.t('filters.sent'),
      falseLabel: I18n.t('filters.not_sent'),
      active: filters.active.sent,
      value: filters.values.sent,
      onChange: onChangeFilter('sent'),
      onToggle: onToggleFilter('sent')
    });

    if (!globalTerritoriesApplied && !inspectionId && !workOrderId) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: onChangeFilter('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: onChangeFilter('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    return list;
  }, [
    filters,
    dispatch,
    buildingId,
    accountId,
    inspectionId,
    workOrderId,
    statusesForSelect,
    categoriesForSelect,
    isQbSyncEnabled,
    isRecurrenceEnabled,
    globalTerritoriesApplied
  ]);

  return filtersList;
};

export default useFilters;
